import { AxiosResponse } from 'axios';
import i18next from 'i18next';
import { hideLoading, showLoading } from 'react-redux-loading-bar';
import { go, replace } from 'react-router-redux';
import { call, put } from 'redux-saga/effects';
import { store } from 'src/app/store';
import CheckCircleBig from 'src/assets/check-circle-big.svg';
import {
  ApiPaginationResponse,
  ServiceAccountTableQueryParameters,
} from 'src/components/ui/Table/Table.interface';
import { routes } from 'src/routes';
import { cleanQueryParams } from 'src/utils/useQueryParams';

import instance, { CustomAxiosError } from '../../axios';
import { apiBaseRoutes, apiRoutes } from '../../constants/apiConstants';
import { Errors } from '../../constants/errorConstants';
import {
  addError,
  addModal,
  addSnackbar,
  removeModal,
  setGlobalLoading,
  startLoading,
  stopLoading,
} from '../../features/globalSlice';
import {
  addServiceAccount,
  bulkEditAutoAssign,
  changeServiceAccountStatus,
  editServiceAccountDetails,
  getAutoAssignServiceAccountList,
  getServiceAccountDetails,
  getServiceAccountList,
  removeServiceAccount,
  setAutoAssignServiceAccountList,
  setServiceAccountDetails,
  setServiceAccountList,
  setServiceAccountStatus,
} from '../actions/serviceAccountActions';
import { Company } from '../models/Company';
import { ModalType } from '../models/Modal';
import {
  AccountStatus,
  ServiceAccount,
  ServiceAccountAccess,
} from '../models/ServiceAccount';
import { SnackbarType } from '../models/Snackbar';
import { ApiResponse } from '.';
import { ResetPasswordHelper } from './authSaga';

/* Get service account list */
export function* ServiceAccountGetListSaga(
  action: ReturnType<typeof getServiceAccountList>
): Generator {
  yield put(showLoading());
  yield put(startLoading({ actionType: action.type }));
  try {
    const paramsString = cleanQueryParams<
      ServiceAccountTableQueryParameters<void, ServiceAccount>
    >(action.payload.params);

    const response = (yield instance.get(
      `${apiBaseRoutes.MANUFACTURER}${apiRoutes.SERVICE}${paramsString}`
    )) as AxiosResponse<ApiPaginationResponse<ServiceAccount[]>>;

    yield put(setServiceAccountList(response?.data));
  } catch (e) {
    const error = e as CustomAxiosError;
    yield put(
      addSnackbar({
        type: SnackbarType.ERROR,
        body: error.response?.data?.message || Errors.UNKNOWN_ERROR,
        snackKey: Date.now(),
      })
    );
  } finally {
    yield put(hideLoading());
    yield put(stopLoading({ actionType: action.type }));
  }
}

/* Get service account details */
export function* ServiceAccountGetDetailsSaga(
  action: ReturnType<typeof getServiceAccountDetails>
): Generator {
  yield put(showLoading());
  yield put(startLoading({ actionType: action.type }));
  try {
    const response = (yield instance.get(
      `${apiBaseRoutes.MANUFACTURER}${apiRoutes.SERVICE}/${action.payload?.serviceId}`
    )) as AxiosResponse<ApiResponse<ServiceAccount>>;

    yield put(setServiceAccountDetails(response.data?.data));
  } catch (e) {
    const error = e as CustomAxiosError;
    yield put(
      addSnackbar({
        type: SnackbarType.ERROR,
        body: error.response?.data?.message || Errors.UNKNOWN_ERROR,
        snackKey: Date.now(),
      })
    );
  } finally {
    yield put(hideLoading());
    yield put(stopLoading({ actionType: action.type }));
  }
}

/* Enable/disable service account */
export function* ServiceEnableDisableSaga(
  serviceAccountId: string,
  enabled: boolean
): Generator {
  try {
    const response = (yield instance.put(
      `${apiBaseRoutes.MANUFACTURER}/${serviceAccountId}${
        enabled ? apiRoutes.ENABLE : apiRoutes.DISABLE
      }`
    )) as AxiosResponse<ApiResponse<ServiceAccount>>;

    return response;
  } catch (e) {
    const error = e as CustomAxiosError;
    yield put(
      addSnackbar({
        type: SnackbarType.ERROR,
        title: i18next.t('service_account:account_status'),
        body: error.response?.data?.message || Errors.UNKNOWN_ERROR,
        snackKey: Date.now(),
      })
    );
  }
}

/* Change service account status */
export function* ServiceChangeAccountStatusSaga(
  action: ReturnType<typeof changeServiceAccountStatus>
): Generator {
  yield put(showLoading());
  yield put(
    startLoading({ actionType: action.type, id: action.payload?.service?.id })
  );
  try {
    const response = (yield call(
      ServiceEnableDisableSaga,
      action?.payload?.service?.id,
      action?.payload?.enabled
    )) as AxiosResponse<ApiResponse<ServiceAccount>>;

    if (response) {
      const enabled = response?.data?.data?.enabled;
      const service = `${response?.data?.data?.title} (${action?.payload?.service?.company?.title})`;

      yield put(
        setServiceAccountStatus({
          service: action.payload?.service,
          enabled: enabled,
        })
      );
      yield put(
        addSnackbar({
          type: SnackbarType.SUCCESS,
          title: enabled
            ? i18next.t('service_account:account_activated')
            : i18next.t('service_account:account_deactivated'),
          body: enabled
            ? i18next.t('service_account:account_activated_details', {
                service,
              })
            : i18next.t('service_account:account_deactivated_details', {
                service,
              }),
          snackKey: Date.now(),
        })
      );
    }
  } finally {
    yield put(hideLoading());
    yield put(
      stopLoading({ actionType: action.type, id: action.payload?.service?.id })
    );
  }
}

/* Link company with service account */
export function* ServiceLinkCompanySaga(
  company: Company,
  serviceAccountId: string
): Generator {
  try {
    const response = (yield instance.put(
      `${apiBaseRoutes.COMPANY}/${serviceAccountId}/${company?.id}`
    )) as AxiosResponse<ApiResponse<ServiceAccount>>;

    return response;
  } catch (e) {
    const error = e as CustomAxiosError;
    const errorMessage = error.response?.data?.message || Errors.UNKNOWN_ERROR;
    yield put(
      addSnackbar({
        type: SnackbarType.ERROR,
        title: i18next.t('service_account:assign_company'),
        body: errorMessage,
        snackKey: Date.now(),
      })
    );
  }
}

/* Edit service account */
export function* ServiceEditAccountSaga(
  action: ReturnType<typeof editServiceAccountDetails>
): Generator {
  yield put(showLoading());
  yield put(setGlobalLoading(true));
  try {
    const response = (yield instance.put(
      `${apiBaseRoutes.MANUFACTURER}${apiRoutes.SERVICE}/${action.payload?.id}`,
      {
        title: action.payload?.title,
        phone: action.payload?.phone,
        restricted: action.payload?.access === ServiceAccountAccess.LIMITED,
        notes: action.payload?.notes,
        companyId: action.payload?.company?.id,
      }
    )) as AxiosResponse<ApiResponse<ServiceAccount>>;

    const responseChangeStatus = (yield call(
      ServiceEnableDisableSaga,
      action?.payload?.id,
      action.payload?.enabled === AccountStatus.ACTIVATED
    )) as AxiosResponse<ApiResponse<ServiceAccount>>;

    const status = responseChangeStatus
      ? responseChangeStatus?.data?.data?.enabled
      : response?.data?.data?.enabled;

    yield put(
      setServiceAccountDetails({ ...response?.data?.data, enabled: status })
    );
    yield put(
      addSnackbar({
        type: SnackbarType.SUCCESS,
        title: i18next.t('common:changes_saved'),
        body: i18next.t('common:changes_saved_success'),
        snackKey: Date.now(),
      })
    );
  } catch (e) {
    const error = e as CustomAxiosError;
    const errorMessage = error.response?.data?.message || Errors.UNKNOWN_ERROR;
    if (typeof error.response?.data?.message === 'string') {
      yield put(
        addSnackbar({
          type: SnackbarType.ERROR,
          body: errorMessage,
          snackKey: Date.now(),
        })
      );
    } else {
      yield put(
        addError({
          actionType: action.type,
          error: errorMessage,
        })
      );
    }
  } finally {
    yield put(hideLoading());
    yield put(setGlobalLoading(false));
  }
}

/* Add service account */
export function* ServiceAddAccountSaga(
  action: ReturnType<typeof addServiceAccount>
): Generator {
  yield put(showLoading());
  yield put(setGlobalLoading(true));
  try {
    const response = (yield instance.post(
      `${apiBaseRoutes.USER}${apiRoutes.CREATE}`,
      {
        eMail: action.payload?.eMail,
        /* TO-DO remove when BE changes settings */
        password: 'FumisWeb_12',
        title: action.payload?.title,
        phone: action.payload?.phone,
        enabled: true,
        restricted: action.payload?.access === ServiceAccountAccess.LIMITED,
        companyId: action.payload?.company?.id,
        notes: action.payload?.notes,
      }
    )) as AxiosResponse<ApiResponse<ServiceAccount>>;

    yield call(ResetPasswordHelper, response?.data?.data?.id);

    yield put(
      addModal({
        title: i18next.t('service_account:account_created'),
        body: i18next.t('service_account:account_created_details', {
          account: `${action.payload?.title} (${action.payload?.company?.title})`,
        }),
        modalType: ModalType.SUCCESS,
        icon: CheckCircleBig,
        onBackdropClose: false,
        primaryButtonText: i18next.t('service_account:create_another_account'),
        secondaryButtonText: i18next.t('service_account:back_to_personnel'),
        primaryAction: () => {
          store.dispatch(
            replace(routes.NEW_SERVICE_ACCOUNT, { forcePush: true })
          );
          store.dispatch(go(0));
          store.dispatch(replace(routes.NEW_SERVICE_ACCOUNT, null));
          store.dispatch(removeModal());
        },
        secondaryAction: () => {
          store.dispatch(removeModal());
          store.dispatch(replace(routes.SERVICE_ACCOUNTS, { forcePush: true }));
        },
      })
    );
  } catch (e) {
    const error = e as CustomAxiosError;
    const errorMessage = error.response?.data?.message || Errors.UNKNOWN_ERROR;
    if (typeof error.response?.data?.message === 'string') {
      yield put(
        addSnackbar({
          type: SnackbarType.ERROR,
          body: errorMessage,
          snackKey: Date.now(),
        })
      );
    } else {
      yield put(
        addError({
          actionType: action.type,
          error: errorMessage,
        })
      );
    }
  } finally {
    yield put(hideLoading());
    yield put(setGlobalLoading(false));
  }
}

/* Remove service account */
export function* ServiceRemoveSaga(
  action: ReturnType<typeof removeServiceAccount>
): Generator {
  yield put(showLoading());
  yield put(startLoading({ actionType: action.type }));
  try {
    (yield instance.delete(
      `${apiBaseRoutes.USER}/${action.payload?.id}`
    )) as AxiosResponse<ApiResponse<ServiceAccount>>;

    yield put(removeModal());
    yield put(replace(routes.SERVICE_ACCOUNTS, { forcePush: true }));
    yield put(
      addSnackbar({
        type: SnackbarType.SUCCESS,
        title: i18next.t('service_account:account_removed'),
        body: i18next.t('service_account:account_removed_details', {
          account: `${action.payload?.title} (${action?.payload?.company?.title})`,
        }),
        snackKey: Date.now(),
      })
    );
  } catch (e) {
    yield put(removeModal());
    const error = e as CustomAxiosError;
    const errorMessage = error.response?.data?.message || Errors.UNKNOWN_ERROR;
    yield put(
      addSnackbar({
        type: SnackbarType.ERROR,
        body: errorMessage,
        snackKey: Date.now(),
      })
    );
  } finally {
    yield put(hideLoading());
    yield put(stopLoading({ actionType: action.type }));
  }
}

/* Get auto assign service account list */
export function* ServiceAccountGetAutoAssignListSaga(
  action: ReturnType<typeof getAutoAssignServiceAccountList>
): Generator {
  yield put(showLoading());
  yield put(startLoading({ actionType: action.type }));
  try {
    const paramsString = cleanQueryParams<
      ServiceAccountTableQueryParameters<void, ServiceAccount>
    >({ ...action.payload.params, autoAssign: 1 });

    const response = (yield instance.get(
      `${apiBaseRoutes.MANUFACTURER}${apiRoutes.SERVICE}${paramsString}`
    )) as AxiosResponse<ApiPaginationResponse<ServiceAccount[]>>;

    yield put(
      setAutoAssignServiceAccountList({
        ...response?.data,
        data: response?.data?.data?.filter((s) => s?.enabled),
      })
    );
  } catch (e) {
    const error = e as CustomAxiosError;
    yield put(
      addSnackbar({
        type: SnackbarType.ERROR,
        body: error.response?.data?.message || Errors.UNKNOWN_ERROR,
        snackKey: Date.now(),
      })
    );
  } finally {
    yield put(hideLoading());
    yield put(stopLoading({ actionType: action.type }));
  }
}

/* Bulk edit auto assign list */
export function* ServiceAccountBulkEditAutoAssignSaga(
  action: ReturnType<typeof bulkEditAutoAssign>
): Generator {
  yield put(showLoading());
  yield put(startLoading({ actionType: action.type }));
  try {
    yield instance.put(
      `${apiBaseRoutes.MANUFACTURER}${apiRoutes.AUTO_ASSIGN}${apiRoutes.BULK}`,
      {
        updates: Object.entries(action?.payload).map(([key, value]) => {
          return { id: key, enabled: value };
        }),
      }
    );

    yield put(removeModal());
    yield put(replace(routes.AUTO_ASSIGN));
  } catch (e) {
    const error = e as CustomAxiosError;
    yield put(
      addSnackbar({
        type: SnackbarType.ERROR,
        body: error.response?.data?.message || Errors.UNKNOWN_ERROR,
        snackKey: Date.now(),
      })
    );
  } finally {
    yield put(hideLoading());
    yield put(stopLoading({ actionType: action.type }));
  }
}
